import React, { useState } from 'react';

import './InvoiceModal.css';
import './Modal.css';

function InvoiceModal({ onClose, onSave, initialData = null }) {
  const [formData, setFormData] = useState({
    invoice_date: initialData?.invoice_date ? initialData.invoice_date.split('T')[0].replace(/-/g, '') : new Date().toISOString().split('T')[0].replace(/-/g, ''),
    invoice_prefix: initialData?.invoice_number ? initialData.invoice_number.split('-')[0] : '',
    invoice_number_suffix: initialData?.invoice_number ? initialData.invoice_number.split('-')[1] : '',
    invoice_type: initialData?.invoice_type || 'A',
    total: initialData?.total || '',
    file: null,
  });

  const [filePreview, setFilePreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, file }));

      // Preview para imágenes
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setFilePreview(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        setFilePreview(null);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.invoice_prefix || !formData.invoice_number_suffix || !formData.total) {
      alert('Por favor complete todos los campos requeridos (fecha, prefijo, número y total)');
      return;
    }

    // Validar fecha de factura
    const dateStr = formData.invoice_date; // formato YYYYMMDD
    const year = parseInt(dateStr.substring(0, 4), 10);
    const month = parseInt(dateStr.substring(4, 6), 10);
    const day = parseInt(dateStr.substring(6, 8), 10);
    const invoiceDate = new Date(year, month - 1, day);
    const today = new Date();
    today.setHours(23, 59, 59, 999);

    if (year < 2020 || year > 2030) {
      alert(`⚠️ El año de la factura (${year}) parece incorrecto. Por favor revisá la fecha.`);
      return;
    }

    if (month < 1 || month > 12 || day < 1 || day > 31 ||
      invoiceDate.getFullYear() !== year || invoiceDate.getMonth() !== month - 1 || invoiceDate.getDate() !== day) {
      alert('⚠️ La fecha de la factura no es válida. Por favor revisala.');
      return;
    }

    if (invoiceDate > today) {
      if (!window.confirm(`⚠️ La fecha de la factura (${day}/${month}/${year}) es posterior a hoy. ¿Desea continuar?`)) {
        return;
      }
    }

    const prefix = formData.invoice_prefix.padStart(4, '0');
    const suffix = formData.invoice_number_suffix.padStart(8, '0');
    const fullNumber = `${prefix}-${suffix}`;

    const formDataToSend = new FormData();
    formDataToSend.append('invoice_date', formData.invoice_date);
    formDataToSend.append('invoice_number', fullNumber);
    formDataToSend.append('invoice_type', formData.invoice_type);
    formDataToSend.append('total', formData.total);

    if (formData.file) {
      formDataToSend.append('file', formData.file);
    }

    setUploading(true);
    try {
      await onSave(formDataToSend);
    } catch (err) {
      // error is handled by parent
    } finally {
      setUploading(false);
    }
  };

  /* Ref for file input trigger */
  const fileInputRef = React.useRef(null);

  const handleFileAreaClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <div className="modal-overlay" onClick={uploading ? undefined : onClose} style={{ zIndex: 1100 }}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ width: '500px' }}>
        <div className="modal-header">
          <h2>{initialData ? 'Editar Factura' : 'Agregar Factura'}</h2>
          <button className="close" onClick={onClose} disabled={uploading}>&times;</button>
        </div>

        <div className="modal-body">
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Fecha de Factura *</label>
              <input
                type="date"
                className="form-control"
                value={formData.invoice_date.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3')}
                onChange={(e) => {
                  const dateStr = e.target.value.replace(/-/g, '');
                  setFormData(prev => ({ ...prev, invoice_date: dateStr }));
                }}
                required
              />
            </div>

            <div className="form-group">
              <label>Número de Factura *</label>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <input
                  type="text"
                  className="form-control"
                  name="invoice_prefix"
                  value={formData.invoice_prefix}
                  onChange={handleChange}
                  placeholder="0001"
                  maxLength="5"
                  style={{ width: '80px', textAlign: 'center' }}
                  required
                />
                <span style={{ fontWeight: 'bold' }}>-</span>
                <input
                  type="text"
                  className="form-control"
                  name="invoice_number_suffix"
                  value={formData.invoice_number_suffix}
                  onChange={handleChange}
                  placeholder="00001234"
                  maxLength="8"
                  style={{ flex: 1 }}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label>Tipo de Factura *</label>
              <select
                className="form-control"
                name="invoice_type"
                value={formData.invoice_type}
                onChange={handleChange}
                required
              >
                <option value="A">A</option>
                <option value="B">B</option>
                <option value="C">C</option>
                <option value="X">X</option>
              </select>
            </div>

            <div className="form-group">
              <label>Total *</label>
              <input
                type="text"
                className="form-control"
                name="total"
                value={formData.total ? new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(formData.total) : ''}
                onChange={(e) => {
                  // Get only digits
                  const val = e.target.value.replace(/\D/g, '');
                  if (!val) {
                    setFormData(prev => ({ ...prev, total: '' }));
                    return;
                  }
                  // Treat as cents (ATM style)
                  const numberValue = parseInt(val, 10) / 100;
                  setFormData(prev => ({ ...prev, total: numberValue.toFixed(2) }));
                }}
                required
                placeholder="$ 0,00"
              />
            </div>

            <div className="form-group">
              <label>Archivo Escaneado (PDF, JPG, PNG)</label>
              <div
                className="file-upload"
                onClick={handleFileAreaClick}
                style={{
                  border: '2px dashed #ddd',
                  padding: '20px',
                  textAlign: 'center',
                  cursor: 'pointer',
                  borderRadius: '4px',
                  backgroundColor: '#f9f9f9',
                  marginTop: '5px'
                }}
              >
                <input
                  type="file"
                  ref={fileInputRef}
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
                <div>
                  {formData.file ? (
                    <div>
                      <p style={{ margin: 0, fontWeight: 'bold', color: '#27ae60' }}>Archivo seleccionado: {formData.file.name}</p>
                      {filePreview && (
                        <img src={filePreview} alt="Preview" style={{ maxWidth: '100%', maxHeight: '150px', marginTop: '10px', borderRadius: '4px', border: '1px solid #ddd' }} />
                      )}
                    </div>
                  ) : (
                    <p style={{ margin: 0, color: '#666' }}>Haz clic aquí para seleccionar un archivo</p>
                  )}
                </div>
              </div>
            </div>

            <div className="modal-actions" style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button type="button" className="btn btn-secondary" onClick={onClose} disabled={uploading}>
                Cancelar
              </button>
              <button type="submit" className="btn btn-primary" disabled={uploading} style={{ position: 'relative', minWidth: '160px' }}>
                {uploading ? (
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                    <span className="invoice-spinner" />
                    Subiendo...
                  </span>
                ) : (
                  initialData ? 'Guardar Cambios' : 'Guardar Factura'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default InvoiceModal;
